import {
  ArrowRight,
  BadgeCheck,
  Blocks,
  BookOpenCheck,
  Check,
  CheckCircle2,
  ChevronDown,
  CirclePlay,
  Code2,
  Cpu,
  Database,
  Gauge,
  LockKeyhole,
  MousePointer2,
  Play,
  Sparkles,
  Trophy,
  Users,
  WandSparkles,
  X,
} from "lucide-react";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { postJson } from "../lib/api";
import type { CourseTier } from "../types/api";
import { Footer } from "../components/Footer";
import { Navbar } from "../components/Navbar";
import { useToast } from "../context/ToastContext";

const faqs = [
  ["Do I need programming experience?", "No. Programmer A starts with Studio basics and builds your Luau skills step by step."],
  ["Is Programmer B included in Programmer A?", "No. Programmer A includes the foundations course. Programmer B unlocks both the foundations and advanced tracks."],
  ["How long do I keep access?", "Both prices shown are one-time payments for lifetime access to the purchased tier and its future lessons."],
  ["Can I learn on a phone?", "The course works on phones and tablets, but you will need a Windows or Mac computer to practise inside Roblox Studio."],
  ["Are the videos downloadable?", "Videos stream inside each lesson. Course notes and code examples can be viewed and copied from any device."],
];

function PricingCard({ tier, price, featured, features }: { tier: CourseTier; price: string; featured?: boolean; features: string[] }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);

  async function choosePlan() {
    if (!user) {
      navigate(`/signup?plan=${tier}`);
      return;
    }
    setLoading(true);
    try {
      const data = await postJson<{ url: string }>("/api/checkout", { plan: tier });
      window.location.assign(data.url);
    } catch (error) {
      showToast(error instanceof Error ? error.message : "Checkout could not be started.", "error");
      setLoading(false);
    }
  }

  return (
    <article className={`pricing-card ${featured ? "pricing-featured" : ""}`}>
      {featured && <span className="popular-badge"><Sparkles size={14} />Best value</span>}
      <div className="pricing-name"><span className={`tier-large tier-${tier}`}>{tier}</span><div><h3>Programmer {tier}</h3><p>{tier === "A" ? "Build a confident foundation" : "Build production-ready systems"}</p></div></div>
      <div className="price"><strong>${price}</strong><span>AUD · one time</span></div>
      <button className={`button button-wide ${featured ? "button-primary" : "button-secondary"}`} onClick={choosePlan} disabled={loading}>
        {loading ? "Opening checkout…" : `Choose Programmer ${tier}`} <ArrowRight size={17} />
      </button>
      <ul>{features.map((feature) => <li key={feature}><CheckCircle2 size={17} />{feature}</li>)}</ul>
    </article>
  );
}

export function LandingPage() {
  const [openFaq, setOpenFaq] = useState(0);

  return (
    <div className="marketing-page">
      <Navbar />
      <main>
        <section className="hero section-shell">
          <div className="hero-glow hero-glow-one" />
          <div className="hero-glow hero-glow-two" />
          <div className="hero-copy">
            <div className="hero-pill"><span /><span>New lessons added as the course grows</span><ArrowRight size={14} /></div>
            <h1>Stop watching.<br /><span>Start building.</span></h1>
            <p>Learn Roblox Studio and Luau by turning real code into working game systems—one focused lesson at a time.</p>
            <div className="hero-actions">
              <Link className="button button-primary button-large" to="/signup">Start learning <ArrowRight size={18} /></Link>
              <a className="button button-ghost button-large" href="#plans"><CirclePlay size={19} />View plans</a>
            </div>
            <div className="hero-proof">
              <span><BadgeCheck size={17} />One-time payment</span>
              <span><BookOpenCheck size={17} />Progress saved</span>
              <span><LockKeyhole size={17} />Secure access</span>
            </div>
          </div>
          <div className="hero-visual" aria-label="Example Luau lesson interface">
            <div className="visual-grid" />
            <div className="studio-panel">
              <div className="studio-toolbar"><div><span /><span /><span /></div><span>TouchDoor.server.lua</span><button><Play size={13} fill="currentColor" />Run</button></div>
              <div className="studio-body">
                <div className="line-numbers">1<br />2<br />3<br />4<br />5<br />6<br />7</div>
                <pre><span className="pink">local</span> door = workspace.Door{`\n\n`}door.Touched:<span className="blue">Connect</span>(<span className="pink">function</span>(hit){`\n  `}<span className="pink">local</span> player = Players:<span className="blue">GetPlayerFromCharacter</span>(hit.Parent){`\n\n  `}<span className="pink">if</span> player <span className="pink">then</span> unlockDoor() <span className="pink">end</span>{`\n`}<span className="pink">end</span>)</pre>
              </div>
              <div className="studio-status"><span><span className="status-dot" />Connected</span><span>Luau</span></div>
            </div>
            <div className="floating-card progress-float"><span><Trophy size={17} /></span><div><small>Course progress</small><strong>68% complete</strong><div className="mini-progress"><i /></div></div></div>
            <div className="floating-card lesson-float"><span><Check size={17} /></span><div><small>Lesson complete</small><strong>Variables in Luau</strong></div></div>
            <div className="cursor-chip"><MousePointer2 size={16} />You build it</div>
          </div>
        </section>

        <section className="trust-strip">
          <span>LEARN</span><i /><span>CODE</span><i /><span>TEST</span><i /><span>BUILD</span><i /><span>REPEAT</span>
        </section>

        <section className="benefits section-shell">
          <div className="section-heading centered"><span className="eyebrow">Built for real progress</span><h2>A clear path from first script<br />to complete game systems.</h2><p>No endless playlists. Every lesson gives you something concrete to understand, practise and prove.</p></div>
          <div className="benefit-grid">
            <article className="benefit-card benefit-wide">
              <div><span className="feature-icon icon-blue"><Code2 /></span><h3>Code beside the lesson</h3><p>Copy clean, explained Luau examples and put them straight into Roblox Studio.</p></div>
              <div className="mini-code"><span><i>1</i><b>local</b> coins = 10</span><span><i>2</i><b>local</b> bonus = 5</span><span><i>3</i><em>print</em>(coins + bonus)</span><small>Output <strong>15</strong></small></div>
            </article>
            <article className="benefit-card"><span className="feature-icon icon-purple"><Gauge /></span><h3>Know where you stand</h3><p>Your dashboard saves visits, quiz scores and lesson completion.</p><div className="radial-demo"><strong>72%</strong><span>Programmer A</span></div></article>
            <article className="benefit-card"><span className="feature-icon icon-green"><Trophy /></span><h3>Learn, then prove it</h3><p>Short progress tests turn watching into active recall.</p><div className="answers-demo"><span><Check />Correct</span><span><X />Review this</span></div></article>
          </div>
        </section>

        <section className="curriculum-section" id="curriculum">
          <div className="section-shell curriculum-shell">
            <div className="curriculum-copy"><span className="eyebrow">Two learning tracks</span><h2>Begin with the building blocks.<br />Graduate to real systems.</h2><p>Move at your own pace. Programmer B includes every foundations lesson, so there are no gaps when you step up.</p><Link className="text-link" to="/signup">Explore the course <ArrowRight size={16} /></Link></div>
            <div className="curriculum-tracks">
              <article className="track-card track-a"><header><span className="tier-large tier-A">A</span><div><small>FOUNDATIONS</small><h3>Programmer A</h3></div><span>01</span></header><ul><li><Check />Studio tour & workflow</li><li><Check />Variables and data types</li><li><Check />Functions and events</li><li><Check />Your first interactive system</li></ul></article>
              <article className="track-card track-b"><header><span className="tier-large tier-B">B</span><div><small>ADVANCED</small><h3>Programmer B</h3></div><span>02</span></header><ul><li><Check />Client–server architecture</li><li><Check />DataStores and player data</li><li><Check />Modular system design</li><li><Check />Secure remote events</li></ul><span className="includes-a"><Blocks size={15} />Includes Programmer A</span></article>
            </div>
          </div>
        </section>

        <section className="workflow section-shell">
          <div className="section-heading"><span className="eyebrow">How it works</span><h2>Small steps. Visible progress.</h2></div>
          <div className="workflow-grid">
            <article><span>01</span><div className="workflow-icon"><CirclePlay /></div><h3>Watch</h3><p>Focused videos explain one idea without wasting your time.</p></article>
            <article><span>02</span><div className="workflow-icon"><WandSparkles /></div><h3>Build</h3><p>Use the notes and code to recreate the system in Studio.</p></article>
            <article><span>03</span><div className="workflow-icon"><BadgeCheck /></div><h3>Test</h3><p>Answer the progress test and see exactly what to review.</p></article>
            <article><span>04</span><div className="workflow-icon"><Cpu /></div><h3>Level up</h3><p>Carry each skill into more advanced game architecture.</p></article>
          </div>
        </section>

        <section className="plans-section" id="plans">
          <div className="section-shell">
            <div className="section-heading centered"><span className="eyebrow">Simple pricing</span><h2>Pick your starting point.</h2><p>Pay once. Learn at your pace. Keep access to the lessons in your tier.</p></div>
            <div className="pricing-grid">
              <PricingCard tier="A" price="3.50" features={["Complete Programmer A course", "Embedded video lessons", "Notes and copyable code", "Progress quizzes", "Saved lesson progress"]} />
              <PricingCard tier="B" price="7.50" featured features={["Everything in Programmer A", "Complete Programmer B course", "Advanced architecture lessons", "All quizzes and examples", "Future lessons in both tracks"]} />
            </div>
            <div className="comparison-table">
              <div className="comparison-row comparison-head"><span>What’s included</span><strong>Programmer A</strong><strong>Programmer B</strong></div>
              {[
                ["Beginner foundations", true, true],
                ["Advanced systems", false, true],
                ["Interactive progress tests", true, true],
                ["Saved progress dashboard", true, true],
                ["Both course tracks", false, true],
              ].map(([label, a, b]) => <div className="comparison-row" key={String(label)}><span>{label}</span><span>{a ? <CheckCircle2 /> : "—"}</span><span>{b ? <CheckCircle2 /> : "—"}</span></div>)}
            </div>
          </div>
        </section>

        <section className="faq-section section-shell" id="faq">
          <div className="faq-intro"><span className="eyebrow">Questions, answered</span><h2>Before you start.</h2><p>Still unsure? Sign up first—you only pay when you choose a course.</p><div className="faq-contact"><Users /><div><strong>Built for new Roblox developers</strong><span>Clear language. Practical examples. No assumed experience.</span></div></div></div>
          <div className="faq-list">
            {faqs.map(([question, answer], index) => <article className={openFaq === index ? "faq-open" : ""} key={question}><button onClick={() => setOpenFaq(openFaq === index ? -1 : index)}><span>{question}</span><ChevronDown /></button>{openFaq === index && <p>{answer}</p>}</article>)}
          </div>
        </section>

        <section className="final-cta section-shell">
          <div className="cta-grid" /><span className="feature-icon icon-blue"><Database /></span><h2>Your first working system<br />starts with one lesson.</h2><p>Create your student account and choose the track that fits where you are now.</p><Link className="button button-primary button-large" to="/signup">Start learning today <ArrowRight size={18} /></Link>
        </section>
      </main>
      <Footer />
    </div>
  );
}
